import React, { Component } from "react";
import { PlanRate, TilgungsReturnData } from "../../lib/Tilgungsplanrechner";
import Content from "../common/Content";
import {
  TableBox,
  TD as DefaultTD,
  TH as DefaultTH,
  TR
} from "../sections/Table";
import { RecalculatorProgram } from "./index";
import styled, { css } from "styled-components";
import { colors, font, fontSizing, media, spacing } from "../theme";
import { yearOrYears } from "../../lib/util";
import ScrollIntoView from "../common/ScrollIntoView";

type Props = {
  calc: TilgungsReturnData;
  choosenProgram: RecalculatorProgram;
};
const ResultBox = styled.div``;
const TH = styled(DefaultTH)`
  @media print {
    padding: 0.1em;
    font-size: 16px;
  }
`;
const TD = styled(DefaultTD)`
  @media print {
    padding: 0.1em;
    font-size: 14px;
    text-align: left;
  }
`;
const ResultRow = styled.div<{ hideTiny?: boolean; center?: boolean }>`
  display: flex;
  &:nth-child(2n-1) {
    background-color: ${colors.lightGrey};
  }
  @media screen and ${media.tiny} {
    flex-wrap: wrap;
    ${p =>
      p.hideTiny &&
      css`
        display: none;
      `}
  }
  ${p =>
    p.center &&
    css`
      justify-content: center;
      align-items: center;
    `}
`;
const ResultCell = styled.div<{ bold?: boolean }>`
  width: 20%;
  flex-shrink: 0;
  hyphens: auto;
  ${fontSizing("label", 20)}
  padding: 1em 0.5em;
  text-align: center;
  font-weight: ${p => (p.bold ? font.weightBold : font.weightRegular)};
  font-family: ${font.main};
  overflow: auto;
  @media screen and ${media.mobile} {
    ${fontSizing("label", 18)}
  }

  @media screen and ${media.tiny} {
    width: 50%;
    &:first-child {
      width: 100%;
      font-weight: ${font.weightBold};
    }
  }
  @media print {
    padding: 0.1em;
    font-size: 14px;
  }
`;
const StyledContent = styled(Content)`
  ${spacing("margin-top", 64)};
  @media screen and ${media.mobile} {
    padding: 0;
  }
  @media print {
    display:block;
  }
`;
const TinyInfo = styled.span<{ bold?: boolean }>`
  display: none;
  @media screen and ${media.tiny} {
    display: inline-block;
    margin-left: 1ch;
    font-weight: ${p => (p.bold ? font.weightBold : font.weightRegular)};
    opacity: 0.8;
  }
`;
export default function ResultView(props: Props) {
  return (
    <>
      {props.calc && (
        <>
          <ScrollIntoView key={JSON.stringify(props.calc)} />
          <StyledContent padded>
            <TableBox>
              <thead>
                <TR>
                  <TH colSpan={2}>Ihr pers&ouml;nlicher Tilgungsplan:</TH>
                </TR>
              </thead>
              <tbody>
                <TR striped>
                  <TD bold>Programm:</TD>
                  <TD>{props.choosenProgram.name}</TD>
                </TR>
                <TR striped>
                  <TD bold>Zinssatz:</TD>
                  <TD>{props.calc.overview.zinssatz.toLocaleString("de")} %</TD>
                </TR>
                <TR striped>
                  <TD bold>Zinsbindung:</TD>
                  <TD>
                    {props.calc.overview.zinsbindung}{" "}
                    {yearOrYears(props.calc.overview.zinsbindung)}
                  </TD>
                </TR>
                <TR striped>
                  <TD bold>Darlehensbetrag:</TD>
                  <TD>
                    {props.calc.overview.darlBetrag.toLocaleString("de", {
                      style: "currency",
                      currency: "EUR",
                      currencyDisplay: "symbol"
                    })}
                  </TD>
                </TR>
                <TR striped>
                  <TD bold>Darlehenslaufzeit:</TD>
                  <TD>
                    {props.calc.overview.laufzeit}{" "}
                    {yearOrYears(props.calc.overview.laufzeit)}
                  </TD>
                </TR>
                <TR striped>
                  <TD bold>Tilgungsfreie Anlaufjahre:</TD>
                  <TD>
                    {props.calc.overview.tilgunsfreieJahre}{" "}
                    {yearOrYears(props.calc.overview.tilgunsfreieJahre)}
                  </TD>
                </TR>
                <TR striped>
                  <TD bold>Zahlungsweise:</TD>
                  <TD>monatlich</TD>
                </TR>
                <TR striped>
                  <TD bold>Anf&auml;nglicher Tilgungssatz:</TD>
                  <TD>
                    {props.calc.overview.tilgungssatz.toLocaleString("de")} %
                  </TD>
                </TR>
              </tbody>
            </TableBox>
            <ResultBox>
              <ResultRow hideTiny>
                <ResultCell bold>Monat</ResultCell>
                <ResultCell bold>Annuit&auml;t</ResultCell>
                <ResultCell bold>davon Zinsen</ResultCell>
                <ResultCell bold>davon Tilgung</ResultCell>
                <ResultCell bold>Restschuld</ResultCell>
              </ResultRow>
              {props.calc.plan.map((rate, i) => (
                <React.Fragment key={i}>
                  <ResultRow>
                    <ResultCell>
                      {rate.rate} <TinyInfo bold>Monat</TinyInfo>
                    </ResultCell>
                    <ResultCell>
                      {rate.annuitaet.toLocaleString("de", {
                        style: "currency",
                        currency: "EUR",
                        currencyDisplay: "symbol"
                      })}{" "}
                      <TinyInfo>Annuit&auml;t</TinyInfo>
                    </ResultCell>
                    <ResultCell>
                      {rate.zinsen.toLocaleString("de", {
                        style: "currency",
                        currency: "EUR",
                        currencyDisplay: "symbol"
                      })}{" "}
                      <TinyInfo>davon Zinsen</TinyInfo>
                    </ResultCell>
                    <ResultCell>
                      {rate.tilgung.toLocaleString("de", {
                        style: "currency",
                        currency: "EUR",
                        currencyDisplay: "symbol"
                      })}{" "}
                      <TinyInfo>davon Tilgung</TinyInfo>
                    </ResultCell>
                    <ResultCell>
                      {rate.restschuld.toLocaleString("de", {
                        style: "currency",
                        currency: "EUR",
                        currencyDisplay: "symbol"
                      })}{" "}
                      <TinyInfo>Restschuld</TinyInfo>
                    </ResultCell>
                  </ResultRow>
                  {(i + 1) % 12 === 0 && i !== 0 && (
                    <ResultRow>
                      <YearLine {...props} i={i} />
                    </ResultRow>
                  )}
                </React.Fragment>
              ))}
              <ResultRow>
                <SumLine {...props} />
              </ResultRow>
              {props.calc.overview.zinsbindung <
                props.calc.overview.laufzeit && (
                <ResultRow center>
                  <span>
                    Der weitere Verlauf der Finanzierung bis zum Laufzeitende
                    ist abhängig vom dann vereinbarten Zinssatz für die
                    Anschlussfinanzierung.
                  </span>
                </ResultRow>
              )}
            </ResultBox>
          </StyledContent>
        </>
      )}
    </>
  );
}

const YearLine: React.FC<Props & { i: number }> = ({ calc, i }) => {
  const rates = calc.plan;
  const year = (i / 12).toFixed(0);
  const sumValues = Array(12)
    .fill(null)
    .reduce(
      (acc, el, j) => {
        return {
          annuitaet: acc.annuitaet + rates[i - j].annuitaet,
          zinsen: acc.zinsen + rates[i - j].zinsen,
          tilgung: acc.tilgung + rates[i - j].tilgung
        };
      },
      {
        annuitaet: 0,
        zinsen: 0,
        tilgung: 0
      }
    );
  return (
    <>
      <ResultCell bold>{year}. Jahr</ResultCell>
      <ResultCell bold>
        {sumValues.annuitaet.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>Annuit&auml;t</TinyInfo>
      </ResultCell>

      <ResultCell bold>
        {sumValues.zinsen.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>davon Zinsen</TinyInfo>
      </ResultCell>

      <ResultCell bold>
        {sumValues.tilgung.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>davon Tilgung</TinyInfo>
      </ResultCell>
      <ResultCell bold>
        {rates[i].restschuld.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>Restschuld</TinyInfo>
      </ResultCell>
    </>
  );
};
const SumLine: React.FC<Props> = ({ calc }) => {
  const rates = calc.plan;
  const sumValues = rates.reduce(
    (acc, el) => {
      return {
        annuitaet: acc.annuitaet + el.annuitaet,
        zinsen: acc.zinsen + el.zinsen,
        tilgung: acc.tilgung + el.tilgung
      };
    },
    {
      annuitaet: 0,
      zinsen: 0,
      tilgung: 0
    }
  );
  return (
    <>
      <ResultCell bold>Gesamtsummen</ResultCell>
      <ResultCell bold>
        {sumValues.annuitaet.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>Annuit&auml;t</TinyInfo>
      </ResultCell>

      <ResultCell bold>
        {sumValues.zinsen.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>davon Zinsen</TinyInfo>
      </ResultCell>

      <ResultCell bold>
        {sumValues.tilgung.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>davon Tilgung</TinyInfo>
      </ResultCell>
      <ResultCell bold>
        {rates[rates.length - 1].restschuld.toLocaleString("de", {
          style: "currency",
          currency: "EUR",
          currencyDisplay: "symbol"
        })}{" "}
        <TinyInfo>Restschuld</TinyInfo>
      </ResultCell>
    </>
  );
};
